import React, { useState, Fragment, createElement } from 'react';
import { Search, Eye, Filter, Package, Clock, Truck, CheckCircle, Sparkles, FileText, Download, ExternalLink } from 'lucide-react';
import { DashboardNav } from '../components/dashboard/DashboardNav';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { formatZAR } from '../utils/currency';
import { OrderStatus } from '../types/models';
const STATUS_COLORS: Record<OrderStatus | 'Pending Approval', string> = {
  Pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  'Order Accepted': 'bg-blue-50 text-blue-700 border-blue-200',
  Crafting: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Crafting Complete': 'bg-purple-50 text-purple-700 border-purple-200',
  'Preparing Delivery': 'bg-violet-50 text-violet-700 border-violet-200',
  'Collection Ready': 'bg-teal-50 text-teal-700 border-teal-200',
  'On the Way': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  Collected: 'bg-green-50 text-green-700 border-green-200',
  Delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Cancelled: 'bg-red-50 text-red-700 border-red-200',
  'Pending Approval': 'bg-orange-50 text-orange-700 border-orange-200'
};
const STATUS_ICONS: Record<OrderStatus | 'Pending Approval', React.ReactNode> = {
  Pending: <Clock className="w-4 h-4" data-id="element-1421" />,
  'Order Accepted': <CheckCircle className="w-4 h-4" data-id="element-1422" />,
  Crafting: <Package className="w-4 h-4" data-id="element-1423" />,
  'Crafting Complete': <Package className="w-4 h-4" data-id="element-1424" />,
  'Preparing Delivery': <Truck className="w-4 h-4" data-id="element-1425" />,
  'Collection Ready': <Package className="w-4 h-4" data-id="element-1426" />,
  'On the Way': <Truck className="w-4 h-4" data-id="element-1427" />,
  Collected: <CheckCircle className="w-4 h-4" data-id="element-1428" />,
  Delivered: <CheckCircle className="w-4 h-4" data-id="element-1429" />,
  Cancelled: <Clock className="w-4 h-4" data-id="element-1430" />,
  'Pending Approval': <Sparkles className="w-4 h-4" data-id="element-1431" />
};
export function AdminOrders() {
  const {
    orders,
    updateOrderStatus,
    customOrderRequests,
    convertCustomOrderToRegularOrder
  } = useData();
  const {
    user
  } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'All' | 'Pending Approval'>('All');
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  // Handle status change for custom orders
  const handleCustomOrderStatusChange = (customOrderId: string, newStatus: OrderStatus) => {
    convertCustomOrderToRegularOrder(customOrderId, newStatus);
  };
  // Convert custom order requests to order-like format
  const customOrdersAsOrders = customOrderRequests.filter(req => req.status === 'pending' || req.status === 'quoted').map(req => ({
    id: req.id,
    userId: req.userId,
    customerName: req.customerName,
    customerEmail: req.customerEmail,
    customerPhone: req.customerPhone,
    userRegion: undefined,
    items: [{
      productId: 'custom',
      quantity: 1,
      price: req.quotedPrice || 0,
      name: `Custom ${req.category.charAt(0).toUpperCase() + req.category.slice(1)}`
    }],
    status: 'Pending Approval' as const,
    total: req.quotedPrice || 0,
    date: req.createdAt,
    deliveryType: 'delivery' as const,
    deliveryDate: '',
    preferredTime: '',
    deliveryFee: 0,
    isCustomOrder: true,
    customOrderDetails: req.customizationDetails,
    customOrderStatus: req.status
  }));
  // Combine regular orders and custom orders
  const allOrders = [...orders, ...customOrdersAsOrders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const filteredOrders = allOrders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) || order.customerName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const orderStatuses: (OrderStatus | 'Pending Approval')[] = ['Pending Approval', 'Pending', 'Order Accepted', 'Crafting', 'Crafting Complete', 'Preparing Delivery', 'Collection Ready', 'On the Way', 'Collected', 'Delivered', 'Cancelled'];
  const pendingApprovalCount = customOrdersAsOrders.length;
  const handleDownloadProof = (proofOfPayment: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = proofOfPayment;
    link.download = fileName || 'proof-of-payment';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return <div className="min-h-screen bg-rose-50/30 flex" data-id="element-1432">
      <DashboardNav data-id="element-1433" />

      <main className="flex-1 p-4 lg:p-8 overflow-y-auto h-screen" data-id="element-1434">
        <div className="max-w-7xl mx-auto space-y-8" data-id="element-1435">
          <header data-id="element-1436">
            <h1 className="text-2xl font-bold text-gray-900 font-serif" data-id="element-1437">
              Orders Management
            </h1>
            <p className="text-gray-500" data-id="element-1438">
              Track and update customer order statuses across all regions
            </p>
          </header>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-1439">
            <div className="bg-white rounded-xl p-4 border border-rose-100 shadow-sm" data-id="element-1440">
              <div className="flex items-center justify-between" data-id="element-1441">
                <div data-id="element-1442">
                  <p className="text-sm text-gray-600" data-id="element-1443">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-1444">
                    {allOrders.length}
                  </p>
                </div>
                <Package className="w-8 h-8 text-rose-500" data-id="element-1445" />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-orange-100 shadow-sm" data-id="element-1446">
              <div className="flex items-center justify-between" data-id="element-1447">
                <div data-id="element-1448">
                  <p className="text-sm text-gray-600" data-id="element-1449">Custom Orders</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-1450">
                    {pendingApprovalCount}
                  </p>
                </div>
                <Sparkles className="w-8 h-8 text-orange-500" data-id="element-1451" />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-yellow-100 shadow-sm" data-id="element-1452">
              <div className="flex items-center justify-between" data-id="element-1453">
                <div data-id="element-1454">
                  <p className="text-sm text-gray-600" data-id="element-1455">Pending</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-1456">
                    {orders.filter(o => o.status === 'Pending').length}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" data-id="element-1457" />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm" data-id="element-1458">
              <div className="flex items-center justify-between" data-id="element-1459">
                <div data-id="element-1460">
                  <p className="text-sm text-gray-600" data-id="element-1461">In Progress</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-1462">
                    {orders.filter(o => ['Order Accepted', 'Crafting', 'Crafting Complete', 'Preparing Delivery', 'On the Way'].includes(o.status)).length}
                  </p>
                </div>
                <Truck className="w-8 h-8 text-blue-500" data-id="element-1463" />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-green-100 shadow-sm" data-id="element-1464">
              <div className="flex items-center justify-between" data-id="element-1465">
                <div data-id="element-1466">
                  <p className="text-sm text-gray-600" data-id="element-1467">Completed</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-1468">
                    {orders.filter(o => o.status === 'Delivered' || o.status === 'Collected').length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" data-id="element-1469" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden" data-id="element-1470">
            <div className="p-4 border-b border-rose-100 flex flex-col sm:flex-row gap-4" data-id="element-1471">
              <div className="relative flex-1 max-w-md" data-id="element-1472">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} data-id="element-1473" />
                <input type="text" placeholder="Search orders or customers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" data-id="element-1474" />
              </div>
              <div className="flex items-center gap-2" data-id="element-1475">
                <Filter size={20} className="text-gray-400" data-id="element-1476" />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as OrderStatus | 'All' | 'Pending Approval')} className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 text-sm" data-id="element-1477">
                  <option value="All" data-id="element-1478">All Statuses</option>
                  {orderStatuses.map(status => <option key={status} value={status} data-id="element-1479">
                      {status}
                    </option>)}
                </select>
              </div>
            </div>

            <div className="overflow-x-auto" data-id="element-1480">
              <table className="w-full" data-id="element-1481">
                <thead className="bg-rose-50/50" data-id="element-1482">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1483">
                    <th className="px-6 py-4" data-id="element-1484">Order ID</th>
                    <th className="px-6 py-4" data-id="element-1485">Customer</th>
                    <th className="px-6 py-4" data-id="element-1486">Region</th>
                    <th className="px-6 py-4" data-id="element-1487">Delivery Info</th>
                    <th className="px-6 py-4" data-id="element-1488">Date</th>
                    <th className="px-6 py-4" data-id="element-1489">Total</th>
                    <th className="px-6 py-4" data-id="element-1490">Status</th>
                    <th className="px-6 py-4 text-right" data-id="element-1491">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rose-50" data-id="element-1492">
                  {filteredOrders.length === 0 ? <tr data-id="element-1493">
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500" data-id="element-1494">
                        {allOrders.length === 0 ? <div data-id="element-1495">
                            <Package className="w-16 h-16 text-gray-300 mx-auto mb-3" data-id="element-1496" />
                            <p className="font-medium" data-id="element-1497">No orders yet</p>
                            <p className="text-sm mt-1" data-id="element-1498">
                              Orders will appear here once customers place them
                            </p>
                          </div> : 'No orders found matching your search'}
                      </td>
                    </tr> : filteredOrders.map(order => <Fragment key={order.id} data-id="element-1499">
                        <tr className="hover:bg-rose-50/30 transition-colors" data-id="element-1500">
                          <td className="px-6 py-4 text-sm font-medium text-rose-600" data-id="element-1501">
                            #{order.id}
                            {order.isCustomOrder && <span className="ml-2 inline-flex items-center" data-id="element-1502">
                                <Sparkles className="w-3 h-3 text-orange-500" data-id="element-1503" />
                              </span>}
                          </td>
                          <td className="px-6 py-4" data-id="element-1504">
                            <div className="text-sm font-medium text-gray-900" data-id="element-1505">
                              {order.customerName}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-1506">
                              {order.customerEmail}
                            </div>
                            {order.customerPhone && <div className="text-xs text-gray-500" data-id="element-1507">
                                📱 {order.customerPhone}
                              </div>}
                          </td>
                          <td className="px-6 py-4" data-id="element-1508">
                            {order.isCustomOrder && order.status === 'Pending Approval' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800" data-id="element-1509">
                                Custom Order
                              </span> : <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${order.userRegion === 'Limpopo' ? 'bg-blue-100 text-blue-800' : order.userRegion === 'Gauteng' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1510">
                                {order.userRegion || 'N/A'}
                              </span>}
                          </td>
                          <td className="px-6 py-4" data-id="element-1511">
                            {order.isCustomOrder && order.status === 'Pending Approval' ? <div className="text-sm text-orange-600 font-medium" data-id="element-1512">
                                Custom Quote
                              </div> : <>
                                <div className="text-sm text-gray-900 capitalize" data-id="element-1513">
                                  {order.deliveryType}
                                </div>
                                {order.deliveryDate && <>
                                    <div className="text-xs text-gray-500" data-id="element-1514">
                                      {new Date(order.deliveryDate).toLocaleDateString()}
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-1515">
                                      {order.preferredTime}
                                    </div>
                                  </>}
                              </>}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1516">
                            {new Date(order.date).toLocaleDateString()}
                            <div className="text-xs text-gray-400" data-id="element-1517">
                              {new Date(order.date).toLocaleTimeString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-1518">
                            {order.total > 0 ? formatZAR(order.total) : <span className="text-orange-600 text-xs" data-id="element-1519">
                                Pending Quote
                              </span>}
                            <div className="text-xs text-gray-500" data-id="element-1520">
                              {order.items.length} item
                              {order.items.length > 1 ? 's' : ''}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-1521">
                            <select value={order.status} onChange={e => {
                        const newStatus = e.target.value as OrderStatus;
                        if (order.isCustomOrder && order.status === 'Pending Approval') {
                          handleCustomOrderStatusChange(order.id, newStatus);
                        } else {
                          updateOrderStatus(order.id, newStatus);
                        }
                      }} className={`text-xs font-medium px-3 py-1.5 rounded-full border cursor-pointer focus:ring-2 focus:ring-rose-500/20 ${STATUS_COLORS[order.status]}`} data-id="element-1522">
                              {orderStatuses.map(status => <option key={status} value={status} data-id="element-1523">
                                  {status}
                                </option>)}
                            </select>
                          </td>
                          <td className="px-6 py-4 text-right" data-id="element-1524">
                            <button onClick={() => setSelectedOrder(selectedOrder === order.id ? null : order.id)} className="text-gray-400 hover:text-rose-500 transition-colors" data-id="element-1525">
                              <Eye size={18} data-id="element-1526" />
                            </button>
                          </td>
                        </tr>
                        {selectedOrder === order.id && <tr data-id="element-1527">
                            <td colSpan={8} className="px-6 py-4 bg-gray-50" data-id="element-1528">
                              <div className="space-y-4" data-id="element-1529">
                                <h4 className="font-semibold text-gray-900" data-id="element-1530">
                                  Order Details
                                </h4>

                                {/* Proof of Payment Section */}
                                {order.proofOfPayment && <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4" data-id="element-1531">
                                    <div className="flex items-start justify-between mb-3" data-id="element-1532">
                                      <div className="flex items-center gap-2" data-id="element-1533">
                                        <FileText className="w-5 h-5 text-green-600" data-id="element-1534" />
                                        <div data-id="element-1535">
                                          <p className="text-sm font-semibold text-green-900" data-id="element-1536">
                                            Proof of Payment Uploaded
                                          </p>
                                          <p className="text-xs text-green-700" data-id="element-1537">
                                            {order.proofOfPaymentFileName || 'Payment proof'}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex gap-2" data-id="element-1538">
                                        <button onClick={() => window.open(order.proofOfPayment, '_blank')} className="p-2 bg-green-100 hover:bg-green-200 rounded-lg transition-colors" title="View proof" data-id="element-1539">
                                          <ExternalLink className="w-4 h-4 text-green-700" data-id="element-1540" />
                                        </button>
                                        <button onClick={() => handleDownloadProof(order.proofOfPayment!, order.proofOfPaymentFileName || 'proof-of-payment')} className="p-2 bg-green-100 hover:bg-green-200 rounded-lg transition-colors" title="Download proof" data-id="element-1541">
                                          <Download className="w-4 h-4 text-green-700" data-id="element-1542" />
                                        </button>
                                      </div>
                                    </div>
                                    {/* Preview if image */}
                                    {order.proofOfPayment.startsWith('data:image') && <div className="mt-3" data-id="element-1543">
                                        <img src={order.proofOfPayment} alt="Proof of payment" className="max-w-full h-auto max-h-64 rounded-lg border border-green-300" data-id="element-1544" />
                                      </div>}
                                  </div>}

                                {/* Custom Order Details */}
                                {order.isCustomOrder && order.customOrderDetails && <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-id="element-1545">
                                      <p className="text-sm font-medium text-orange-900 mb-2" data-id="element-1546">
                                        Custom Order Specifications:
                                      </p>
                                      <pre className="text-sm text-orange-800 whitespace-pre-wrap font-sans" data-id="element-1547">
                                        {order.customOrderDetails}
                                      </pre>
                                      {order.status === 'Pending Approval' && <p className="text-xs text-orange-700 mt-3 font-medium" data-id="element-1548">
                                          💡 Change status above to move this
                                          order into production
                                        </p>}
                                    </div>}

                                {/* Items */}
                                <div data-id="element-1549">
                                  <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-1550">
                                    Items:
                                  </p>
                                  <div className="space-y-2" data-id="element-1551">
                                    {order.items.map((item, idx) => <div key={idx} className="flex justify-between text-sm bg-white p-3 rounded-lg" data-id="element-1552">
                                        <div data-id="element-1553">
                                          <span className="text-gray-900 font-medium" data-id="element-1554">
                                            {item.name}
                                          </span>
                                          <span className="text-gray-500 ml-2" data-id="element-1555">
                                            × {item.quantity}
                                          </span>
                                          {item.customMessage && <p className="text-xs text-gray-600 italic mt-1" data-id="element-1556">
                                              Message: "{item.customMessage}"
                                            </p>}
                                          {item.isGift && <p className="text-xs text-purple-600 mt-1" data-id="element-1557">
                                              🎁 Gift for: {item.recipientName}{' '}
                                              ({item.recipientPhone})
                                            </p>}
                                        </div>
                                        {item.price > 0 && <span className="font-medium text-gray-900" data-id="element-1558">
                                            {formatZAR(item.price * item.quantity)}
                                          </span>}
                                      </div>)}
                                  </div>
                                </div>

                                {/* Delivery Details */}
                                {!order.isCustomOrder && order.deliveryAddress && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-1559">
                                      <p className="text-sm font-medium text-blue-900 mb-1" data-id="element-1560">
                                        Delivery Address:
                                      </p>
                                      <p className="text-sm text-blue-800" data-id="element-1561">
                                        {order.deliveryAddress}
                                      </p>
                                    </div>}

                                {/* Pricing */}
                                {order.total > 0 && <div className="pt-3 border-t border-gray-200 bg-white p-4 rounded-lg" data-id="element-1562">
                                    <div className="flex justify-between text-sm mb-1" data-id="element-1563">
                                      <span className="text-gray-600" data-id="element-1564">
                                        Subtotal:
                                      </span>
                                      <span className="font-medium" data-id="element-1565">
                                        {formatZAR(order.total - order.deliveryFee)}
                                      </span>
                                    </div>
                                    {order.deliveryFee > 0 && <div className="flex justify-between text-sm mb-1" data-id="element-1566">
                                        <span className="text-gray-600" data-id="element-1567">
                                          Delivery Fee:
                                        </span>
                                        <span className="font-medium" data-id="element-1568">
                                          {formatZAR(order.deliveryFee)}
                                        </span>
                                      </div>}
                                    <div className="flex justify-between font-semibold pt-2 border-t border-gray-200 text-lg" data-id="element-1569">
                                      <span data-id="element-1570">Total:</span>
                                      <span className="text-rose-600" data-id="element-1571">
                                        {formatZAR(order.total)}
                                      </span>
                                    </div>
                                  </div>}
                              </div>
                            </td>
                          </tr>}
                      </Fragment>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>;
}